import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Shield, FileCheck, Users, Scale, Eye, Award } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const registrations = [
  { label: "Section 8 Company", detail: "Registered under Companies Act, 2013 — A Section 8 company is a non-profit entity recognized for promoting social welfare, education, religion, or any other charitable purpose. Unlike trusts or societies, it follows corporate governance standards.", status: "Active" },
  { label: "PAN Number", detail: "XXXXX0000X — Permanent Account Number for tax compliance and financial operations.", status: "Registered" },
  { label: "12A Registration", detail: "Tax exemption on income — Once approved, all income of the organization is exempt from income tax, enabling maximum fund utilization for programs.", status: "Pending" },
  { label: "80G Certification", detail: "Donor tax benefit — Once approved, all donors contributing to BSM Foundation can claim tax deductions under Section 80G of the Income Tax Act.", status: "Pending" },
];

const commitments = [
  { icon: Eye, title: "Financial Transparency", desc: "Regular financial audits and public disclosure of fund utilization." },
  { icon: Scale, title: "Legal Compliance", desc: "Full adherence to all regulatory requirements under Section 8 and applicable laws." },
  { icon: Users, title: "Stakeholder Accountability", desc: "Regular reporting to donors, partners, and the communities we serve." },
  { icon: Shield, title: "Ethical Governance", desc: "Zero-tolerance policy for conflicts of interest and adherence to ethical standards." },
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function GovernancePage() {
  return (
    <div data-testid="governance-page" className="pt-20">
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <p className="overline mb-3">Trust & Accountability</p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">Governance & Transparency</h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-3xl leading-relaxed">
              BSM Welfare Foundation is registered as a Section 8 Company under the Companies Act, ensuring a professional governance framework with a strong emphasis on transparency and accountability.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section data-testid="registrations-section" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="mb-14">
            <p className="overline mb-3">Legal Status</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B201E]">Registration & Compliance</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {registrations.map((reg, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <Card data-testid={`registration-card-${i}`} className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#606C38]/10 flex items-center justify-center shrink-0">
                      <FileCheck size={22} className="text-[#606C38]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-['Outfit'] font-semibold text-[#1B201E]">{reg.label}</h3>
                        <Badge className={`rounded-full text-xs font-medium border-0 ${reg.status === "Active" ? "bg-[#606C38]/10 text-[#606C38]" : "bg-[#E28743]/10 text-[#E28743]"}`}>{reg.status}</Badge>
                      </div>
                      <p className="text-sm text-[#7A827E]">{reg.detail}</p>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 EXPLANATION */}
      <section data-testid="section8-explanation" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Why Section 8?</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B201E] mb-5">What is a Section 8 Company?</h2>
              <p className="text-[#7A827E] text-base leading-relaxed mb-4">
                A Section 8 Company under the Companies Act, 2013 is a non-profit organization registered with the Ministry of Corporate Affairs (MCA). Unlike trusts or societies, it is governed by the same compliance standards as any corporate entity — including mandatory audits, annual filings, and board-level governance.
              </p>
              <p className="text-[#7A827E] text-base leading-relaxed mb-4">
                This structure provides <strong className="text-[#2C3531]">higher credibility</strong> among corporates, government bodies, and international donors. It also ensures <strong className="text-[#2C3531]">strict financial discipline</strong> and <strong className="text-[#2C3531]">transparent operations</strong> — making BSM Welfare Foundation CSR-ready from day one.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              <div className="space-y-4">
                {[
                  { title: "Board of Directors", desc: "Guided by committed individuals combining professional expertise with a deep sense of service." },
                  { title: "Mandatory Compliance", desc: "Annual audits, MCA filings, and regulatory adherence ensure institutional discipline." },
                  { title: "No Profit Distribution", desc: "All surplus funds are reinvested into programs and community welfare — never distributed to members." },
                  { title: "CSR Eligibility", desc: "Corporates can directly route CSR funds through our structured, audit-ready framework." },
                ].map((item, i) => (
                  <div key={i} className="bg-[#F3F0E6] rounded-2xl p-5" data-testid={`governance-detail-${i}`}>
                    <p className="font-['Outfit'] font-semibold text-sm text-[#1B201E] mb-1">{item.title}</p>
                    <p className="text-xs text-[#7A827E] leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section data-testid="commitments-section" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="text-center mb-14">
            <p className="overline mb-3">Our Promise</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B201E]">Commitment to Transparency</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {commitments.map((item, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div data-testid={`commitment-card-${i}`} className="flex gap-5 p-7 rounded-3xl bg-white border border-[#E2E5E3]/50 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
                  <div className="w-12 h-12 rounded-2xl bg-[#8ECAE6]/15 flex items-center justify-center shrink-0">
                    <item.icon size={22} className="text-[#023047]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-1.5">{item.title}</h3>
                    <p className="text-[#7A827E] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
